import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { actions } from 'modules/user';
import { Alert, Button, Form, FormGroup, Input, Label, Modal, ModalBody, ModalHeader, Row, Col } from 'reactstrap';
import 'whatwg-fetch';
import { PulseLoader } from 'react-spinners';
import { baseEnvVars } from 'components/ForumWrapper';
import Api from 'lib/api';

// library imports
import { forumInterfaceFunctions } from '@frn/frn-react-native-discourse/lib/module/components/hooks/forumInterfaceFunctions';

const loadingProps = { size: '40px', color: '#212529' };

const { ZOOM_SERIES_SUBSCRIPTIONS_WEBHOOK_URL } = process.env;

if (!ZOOM_SERIES_SUBSCRIPTIONS_WEBHOOK_URL) {
  // eslint-disable-next-line no-console
  console.error(
    'ZOOM_SERIES_SUBSCRIPTIONS_WEBHOOK_URL is not defined - event email notification preferences will be hidden.'
  );
}

const ZOOM_EVENT_SERIES = [
  { name: 'Table Topics', eventId: '3mJEp23WSqyDIg1_KZnmLA' },
  { name: 'In the Kitchen', eventId: 'Z8KsxXzNS2aOFZDCy1jfOA' },
  { name: 'Questions & Answers', eventId: 'QhGOOXlRQhWY8AOuItVe3Q' },
  { name: 'Community Connection', eventId: '0lGWOFdQR3m2EPKozOhyGQ' },
  { name: 'Member Support', eventId: 'Pk6fUlm9QeeMGrOUA20D3w' }
];

const ForumFieldMapping = {
  email_digests: 'Receive Email Digests?',
  email_level: 'Send me emails about notifications',
  email_messages_level: 'Send me Emails about Private Messages',
  digest_after_minutes: 'Send me a digest of new conversations after',
  allow_private_messages: 'Allow private messages?',
  topic_muted: 'Mute all notifications about this conversation'
};

const ForumEmailLevelSelectorMapping = {
  0: 'Always',
  1: 'Only When Away',
  2: 'Never'
};

const { CRM_BASE_URL } = process.env;
const { SF_API_URL } = process.env;

const CommunicationPreferences = props => {
  const [Forum, setForum] = useState({});
  const [ForumINITIAL, setInitialForum] = useState({});
  const [ForumUnsubscribe, setForumUnsubscribe] = useState({});
  const [ForumUnsubscribeINITIAL, setInitialForumUnsubscribe] = useState({});
  const [error, setError] = useState(false);
  const [message, setMessage] = useState('');
  const [user, setUser] = useState(props.user);

  const [UUID, setUUID] = useState(user.UUID);

  const [zoomSeriesSubscriptions, setZoomSeriesSubscriptions] = useState(null);
  const [zoomSeriesSubscriptionsINITIAL, setInitialZoomSeriesSubscriptions] = useState(null);
  const [zoomSeriesSaveConfirmation, setZoomSeriesSaveConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const dispatch = useDispatch();

  const setCPinState = 'communicationPreferences' in user;

  const baseUserInfo = () => {
    return {
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      FRNPlatformUserId: user.id,
      // not technically accurate, but as of right now, this is the attribute
      // store-and-forward keys off of to know to go to MarketingCloud
      isNewRecord: true
    };
  };

  useEffect(() => {
    if (user) {
      if (!setCPinState || !user.communicationPreferences.Forum) {
        const { setAuthContextFromUser, getUserUnsubscribeOptions } = forumInterfaceFunctions({
          user: user,
          ...(setCPinState && { dispatchForumCommunicationPreferences: actions.dispatchCommunicationPreferences })
        });

        setAuthContextFromUser(
          { ...user, discourseKey: props.callingUser.discourseKey, userId: user.id },
          {
            discourse_api_username_prefix: baseEnvVars.FORUM_DISCOURSE_API_USERNAME_PREFIX,
            base_url: baseEnvVars.FORUM_BASE_URL,
            setForum,
            setInitialForum
          }
        );

        if (props.unsubscribeKey) {
          getUserUnsubscribeOptions(user, {
            base_url: baseEnvVars.FORUM_BASE_URL,
            unsubscribeKey: props.unsubscribeKey
          }).then(unsubResp => {
            if (unsubResp.unsubscribe_options) {
              setForumUnsubscribe(unsubResp.unsubscribe_options);
              setInitialForumUnsubscribe(unsubResp.unsubscribe_options);
            }
            return Promise.resolve(true);
          });
        }
      } else {
        setForum(user.communicationPreferences.Forum);
        setInitialForum(user.communicationPreferences.Forum);
      }
    }
  }, [user]);

  const [userProducts, setUserProducts] = useState(Array.isArray(user.products) ? user.products : null);

  // publicProfileUser (CS/admin viewing someone else's profile) doesn't come with a `products`
  // array, unlike the logged-in user's own Redux state - fetch it directly so WLC membership
  // can be determined for the profile being viewed.
  useEffect(() => {
    const authToken = props.callingUser && props.callingUser.authToken;
    if (Array.isArray(user.products) || !user.id || !authToken) return;

    Api.get(`/users/${user.id}/products`, authToken)
      .then(resp => {
        if (Array.isArray(resp)) setUserProducts(resp);
      })
      .catch(() => {});
  }, [user.id]);

  const isWLCMember = userProducts && userProducts.some(p => p.slug === 'wlc');
  const showZoomSeriesSubscriptions = isWLCMember && Boolean(ZOOM_SERIES_SUBSCRIPTIONS_WEBHOOK_URL);

  const zoomSeriesSubscriptionsUrl =
    ZOOM_SERIES_SUBSCRIPTIONS_WEBHOOK_URL &&
    `${ZOOM_SERIES_SUBSCRIPTIONS_WEBHOOK_URL}?email=${encodeURIComponent(user.email)}`;

  useEffect(() => {
    const authToken = props.callingUser && props.callingUser.authToken;
    if (!authToken || !showZoomSeriesSubscriptions) return;

    Api.get(zoomSeriesSubscriptionsUrl, authToken)
      .then(resp => {
        const subscribedIds = Array.isArray(resp) ? resp : resp && resp.event_ids ? resp.event_ids : [];
        const subscriptions = ZOOM_EVENT_SERIES.reduce((o, { eventId }) => {
          o[eventId] = subscribedIds.includes(eventId);
          return o;
        }, {});
        setZoomSeriesSubscriptions(subscriptions);
        setInitialZoomSeriesSubscriptions(subscriptions);
      })
      .catch(() => {
        setError(true);
        setMessage('There was an error loading your event email subscription preferences.');
      });
  }, [props.callingUser && props.callingUser.authToken, showZoomSeriesSubscriptions]);

  const toggleZoomSeriesSubscription = eventId => {
    setZoomSeriesSaveConfirmation(false);
    setZoomSeriesSubscriptions(prevState => ({
      ...prevState,
      [eventId]: !prevState[eventId]
    }));
  };

  const optInUser = () => {
    // the user has no UUID, so we will have to create one and opt them in
    fetch(`${SF_API_URL}/user`, {
      method: 'POST',
      body: JSON.stringify({
        ...baseUserInfo(),
        entrySource: 'Opt-in from platform',
        newRegistrant: 3 // to allow user to get added to mailing lists
      })
    }).then(resp => {
      /* edge-case: UUID was null in the platform, but a record exists in MarketingCloud, possibly with different data than defaults
       * we wouldn't be able to capture that at this time. */
      if (resp.ok) {
        let respJSON = resp.json().catch(err => ({ success: 'true' }));
        return respJSON.then(json => {
          setUUID(json.uuid);
        });
      } else {
        // an error for whatever reason
        setError(true);
        setMessage('There was an error opting you into our mailing list. Please contact support for assistance.');
      }
    });
  };

  const toggleCheck = (type, preferencesToToggle) => {
    if (!Array.isArray(preferencesToToggle)) preferencesToToggle = [preferencesToToggle];
    if (type === 'Forum') {
      setForum(prevState => ({
        ...prevState,
        ...preferencesToToggle.reduce((o, k) => {
          o[k] = !prevState[k];
          return o;
        }, {})
      }));
    } else if (type === 'ForumUnsubscribe') {
      setForumUnsubscribe(prevState => ({
        ...prevState,
        ...preferencesToToggle.reduce((o, k) => {
          o[k] = !prevState[k];
          return o;
        }, {})
      }));
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const url = `${SF_API_URL}/user`;

    // check if there are any actually updates here that require posting
    let performForumRequest = Object.keys(Forum).some(k => {
      return Forum[k] !== ForumINITIAL[k];
    });

    let performForumUnsubscribeRequest = Object.keys(ForumUnsubscribe).some(k => {
      return ForumUnsubscribe[k] !== ForumUnsubscribeINITIAL[k];
    });

    setIsSaving(true);
    setZoomSeriesSaveConfirmation(false);

    return new Promise((resolve, reject) => {
      if (!performForumRequest) return resolve(true); //nothing to do
      // update preferences in Discourse
      const { setUserPreferences } = forumInterfaceFunctions({
        user: user
      });

      return Promise.resolve(
        setUserPreferences(
          { ...user, discourseKey: props.callingUser.discourseKey, userId: user.id },
          {
            discourse_api_username_prefix: baseEnvVars.FORUM_DISCOURSE_API_USERNAME_PREFIX,
            base_url: baseEnvVars.FORUM_BASE_URL
          },
          Forum
        )
      ).then(success => {
        if (success) {
          setCPinState && dispatch(actions.dispatchCommunicationPreferences('Forum', Forum));
          setInitialForum(Forum);
          setError(false);
          props.toggle();
          return true;
        } else {
          setError(true);
          setMessage('There was an error updating your communication preferrences.');
          return false;
        }
      });
    })
      .then(() => {
        return new Promise((resolve, reject) => {
          if (!performForumUnsubscribeRequest) return resolve(true); //nothing to do
          // update preferences in Discourse for this unsubscribe key
          const { setUserUnsubscribeOptions } = forumInterfaceFunctions({
            user: user
          });

          // map to preferences needed for Discourse
          let ForumUnsubscribeForSubmission = {
            mute_topic: ForumUnsubscribe.topic_muted ? true : false
          };

          return Promise.resolve(
            setUserUnsubscribeOptions(
              user,
              {
                base_url: baseEnvVars.FORUM_BASE_URL,
                unsubscribeKey: props.unsubscribeKey
              },
              ForumUnsubscribeForSubmission
            )
          ).then(success => {
            if (success) {
              setInitialForumUnsubscribe(ForumUnsubscribe);
              setError(false);
              props.toggle();
              return true;
            } else {
              setError(true);
              setMessage('There was an error updating your communication preferrences.');
              return false;
            }
          });
        });
      })
      .then(() => {
        const authToken = props.callingUser && props.callingUser.authToken;
        const performZoomSeriesRequest =
          showZoomSeriesSubscriptions &&
          zoomSeriesSubscriptions &&
          Object.keys(zoomSeriesSubscriptions).some(
            k => zoomSeriesSubscriptions[k] !== zoomSeriesSubscriptionsINITIAL[k]
          );

        if (!performZoomSeriesRequest || !authToken) return true; //nothing to do

        const subscribedEventIds = Object.keys(zoomSeriesSubscriptions).filter(
          eventId => zoomSeriesSubscriptions[eventId]
        );

        return Api.post(
          zoomSeriesSubscriptionsUrl,
          { event_ids: subscribedEventIds, firstName: user.firstName, lastName: user.lastName },
          authToken
        )
          .then(() => {
            setInitialZoomSeriesSubscriptions(zoomSeriesSubscriptions);
            setZoomSeriesSaveConfirmation(true);
            setError(false);
            return true;
          })
          .catch(() => {
            setError(true);
            setMessage('There was an error updating your event email subscription preferences.');
            return false;
          });
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const CommunicationPreferencesFormFieldBase = (
    mapping,
    sourceObject,
    fieldKey,
    formFieldType,
    formFieldName,
    toggledType,
    toggledKeys = null
  ) => {
    return (
      <Row>
        <Col>
          <Label for={fieldKey}>{mapping[fieldKey]}</Label>
        </Col>
        <Col xs={2}>
          <Input
            type={formFieldType}
            name={formFieldName}
            id={fieldKey}
            checked={sourceObject[fieldKey]}
            onChange={() => {
              return [toggleCheck(toggledType, toggledKeys ? toggledKeys : [fieldKey])];
            }}
          />
        </Col>
      </Row>
    );
  };

  const CommunicationPreferencesFormFieldSelect = (mapping, selectorOptions, sourceObject, fieldKey) => {
    return (
      <Row>
        <Col>
          <Label for={fieldKey}>{mapping[fieldKey]}</Label>
        </Col>
        <Col xs={4}>
          <Input
            type="select"
            id={fieldKey}
            value={sourceObject[fieldKey]}
            style={{ marginLeft: '-3rem' }} // empirically chosen for styling
            onChange={e => {
              const { value } = e.target;
              setForum(prevState => ({
                ...prevState,
                [fieldKey]: parseInt(value)
              }));
            }}
          >
            {Object.keys(selectorOptions).map(n => {
              return (
                <option id={`${fieldKey}-${n}`} key={`${fieldKey}-${n}`} value={n}>
                  {selectorOptions[n]}
                </option>
              );
            })}
          </Input>
        </Col>
      </Row>
    );
  };

  const userForumGroups = Forum.userProductGroups;

  return (
    <Modal centered isOpen={props.isOpen} toggle={props.toggle}>
      <ModalHeader toggle={props.toggle}>Communication Preferences</ModalHeader>
      <ModalBody>
        {error ? (
          message ? (
            <Alert color="danger">{message}</Alert>
          ) : (
            <Alert color="danger">There was an error updating your communication preferences.</Alert>
          )
        ) : null}
        <Form>
          {!UUID ? (
            <>
              <Row style={{ justifyContent: 'center', fontSize: '1.25rem' }}>
                <b>You aren't opted-into our mailing list</b>
              </Row>
              <Row style={{ justifyContent: 'center' }}>
                <Button onClick={e => optInUser()}>Opt-In here</Button>
              </Row>
              <Row style={{ fontSize: '1.5rem', 'font-style': 'italic', padding: '0rem 0.625rem' }}>
                <p>
                  By entering your information here, you are agreeing to receive messages from Food Revolution Network.
                  Your email address will NEVER be shared or sold. You are always free to easily unsubscribe at any
                  time. See our <a href="https://foodrevolution.org/privacy-policy/">Privacy Policy</a>
                  {' for more info.'}
                </p>
              </Row>
            </>
          ) : (
            <div className="mb-4">
              <Row className="ml-1" style={{ fontSize: '1.25rem' }}>
                <b>Email Preferences</b>
              </Row>{' '}
              <Button href={`${CRM_BASE_URL}?SubscriberKey=${UUID}`} target="_blank" color="primary">
                Update your mailing list preferences here.
              </Button>
            </div>
          )}
          {Object.keys(Forum).length && userForumGroups.length > 0 ? (
            <>
              <FormGroup>
                {' '}
                <Row className="ml-1">
                  <b>Community Preferences</b>
                </Row>{' '}
                {/*CommunicationPreferencesFormFieldBase(
                  ForumFieldMapping,
                  Forum,
                  'email_digests',
                  'checkbox',
                  'digests',
                  'Forum'
                )
                */}
                {userForumGroups &&
                  process.env.DMABLE_PRODUCTS.split(',').some(p => userForumGroups.map(up => up.name).includes(p)) &&
                  CommunicationPreferencesFormFieldBase(
                    ForumFieldMapping,
                    Forum,
                    'allow_private_messages',
                    'checkbox',
                    'allow_private_messages',
                    'Forum'
                  )}
                {CommunicationPreferencesFormFieldSelect(
                  ForumFieldMapping,
                  ForumEmailLevelSelectorMapping,
                  Forum,
                  'email_level'
                )}
                {/*CommunicationPreferencesFormFieldSelect(ForumFieldMapping, ForumEmailLevelSelectorMapping, Forum, 'email_messages_level')*/}
                {/*CommunicationPreferencesFormFieldSelect(ForumFieldMapping, [some numbers go here], Forum, 'digest_after_minutes')*/}
                {props.unsubscribeKey ? (
                  Object.keys(ForumUnsubscribe).length ? (
                    CommunicationPreferencesFormFieldBase(
                      ForumFieldMapping,
                      ForumUnsubscribe,
                      'topic_muted',
                      'checkbox',
                      'topic_muted',
                      'ForumUnsubscribe'
                    )
                  ) : (
                    <PulseLoader {...loadingProps} />
                  )
                ) : null}
              </FormGroup>
            </>
          ) : userForumGroups && userForumGroups.length === 0 ? null : (
            <>
              <PulseLoader {...loadingProps} />
              {props.isCS && (
                <p style={{ fontSize: '1.2rem' }}>
                  If you are trying to edit another user's preferences and this never loads, it means you might not have
                  access. Please check with the FRN team!
                </p>
              )}
            </>
          )}
          {showZoomSeriesSubscriptions && (
            <FormGroup>
              <Row className="ml-1" style={{ fontSize: '1.25rem' }}>
                <b>Live Event Email Notifications</b>
              </Row>
              {zoomSeriesSubscriptions ? (
                <>
                  {ZOOM_EVENT_SERIES.map(({ name, eventId }) => (
                    <Row key={eventId}>
                      <Col>
                        <Label for={eventId}>{name}</Label>
                      </Col>
                      <Col xs={2}>
                        <Input
                          type="checkbox"
                          name={eventId}
                          id={eventId}
                          checked={zoomSeriesSubscriptions[eventId] || false}
                          onChange={() => toggleZoomSeriesSubscription(eventId)}
                        />
                      </Col>
                    </Row>
                  ))}
                  {zoomSeriesSaveConfirmation && (
                    <Alert color="success">Your event email preferences have been saved.</Alert>
                  )}
                </>
              ) : (
                <PulseLoader {...loadingProps} />
              )}
            </FormGroup>
          )}
        </Form>
        <Button disabled={isSaving} onClick={e => handleSubmit(e)}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </ModalBody>
    </Modal>
  );
};

export { CommunicationPreferences };
